import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { toast } from "sonner";

export default function AuthModal() {
  const { authModalOpen, setAuthModalOpen, loginSuccess } = useApp();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", referral_code: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      setForm((f) => ({ ...f, referral_code: ref }));
      setMode("register");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : { name: form.name, email: form.email, phone: form.phone, password: form.password, referral_code: form.referral_code || null };
      const { data } = await api.post(endpoint, payload);
      loginSuccess(data);
      toast.success(mode === "login" ? "Welcome back!" : "Welcome to the Bull Family! 🎉");
      setForm({ name: "", email: "", phone: "", password: "", referral_code: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={authModalOpen} onOpenChange={setAuthModalOpen}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="auth-modal">
        <DialogTitle className="sr-only">
          {mode === "login" ? "Login" : "Register"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Access your Bull Creations account
        </DialogDescription>
        <div className="bg-[#1D3557] p-6 text-white">
          <div className="flex items-center gap-2.5">
            <img src="/brand/bull-mark.png" alt="Bull Creations" className="h-10 w-auto" />
            <div className="font-display font-extrabold text-2xl">
              BULL<span className="text-[#E63946]">CREATIONS</span>
            </div>
          </div>
          <p className="text-sm text-slate-300 mt-1">
            {mode === "login" ? "Login to your account" : "Create your account"}
          </p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {mode === "register" && (
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                data-testid="auth-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1"
              />
            </div>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="auth-email-input"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1"
            />
          </div>
          {mode === "register" && (
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                data-testid="auth-phone-input"
                required
                inputMode="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="e.g. +91 98765 43210"
                className="mt-1"
              />
            </div>
          )}
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="auth-password-input"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1"
            />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
          {mode === "register" && (
            <div>
              <Label htmlFor="referral">Referral Code <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                id="referral"
                data-testid="auth-referral-input"
                value={form.referral_code}
                onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                placeholder="Have a friend's code? Get 100 bonus points"
                className="mt-1"
              />
            </div>
          )}
          <Button
            type="submit"
            data-testid="auth-submit-button"
            disabled={loading}
            className="w-full bg-[#E63946] hover:bg-[#d12836] h-11 font-semibold"
          >
            {loading ? "Please wait..." : mode === "login" ? "Login" : "Create Account"}
          </Button>
          <p className="text-center text-sm text-slate-500">
            {mode === "login" ? "New to Bull Creations?" : "Already have an account?"}{" "}
            <button
              type="button"
              data-testid="auth-toggle-mode"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-[#E63946] font-semibold hover:underline"
            >
              {mode === "login" ? "Register" : "Login"}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
